import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../api/client";

const AdminPromotions = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState(searchParams.get("filter") || "all");
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [sort, setSort] = useState(searchParams.get("sort") || "newest");

  useEffect(() => {
    const load = async () => {
      try {
        const response = await api.get("/api/admin/promotions");
        setPromotions(response.data?.promotions || []);
      } catch (err) {
        setError(err?.response?.data?.error || "Failed to load promotions");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    setFilter(searchParams.get("filter") || "all");
    setSearch(searchParams.get("q") || "");
    setSort(searchParams.get("sort") || "newest");
  }, [searchParams]);

  const setUrlState = (patch) => {
    const next = { filter, q: search, sort, ...patch };
    const params = new URLSearchParams();
    if (next.filter && next.filter !== "all") params.set("filter", next.filter);
    if (next.q && next.q.trim()) params.set("q", next.q.trim());
    if (next.sort && next.sort !== "newest") params.set("sort", next.sort);
    setSearchParams(params);
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    const rows = promotions.filter((promo) => {
      const matchesFilter =
        filter === "all" ||
        (filter === "active" && promo.isCurrentlyActive) ||
        (filter === "inactive" && !promo.isCurrentlyActive) ||
        promo.type === filter ||
        promo.audience === filter;
      const sellerName = promo.seller?.storeName || promo.seller?.name || "";
      const matchesSearch =
        !query ||
        promo.title?.toLowerCase().includes(query) ||
        promo.code?.toLowerCase().includes(query) ||
        sellerName.toLowerCase().includes(query);
      return matchesFilter && matchesSearch;
    });
    return [...rows].sort((a, b) => {
      if (sort === "usage-high") return Number(b.usageCount || 0) - Number(a.usageCount || 0);
      if (sort === "ending-soon") return new Date(a.endDate || "2999-01-01") - new Date(b.endDate || "2999-01-01");
      if (sort === "seller") return String(a.seller?.storeName || "").localeCompare(String(b.seller?.storeName || ""));
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    });
  }, [filter, promotions, search, sort]);

  const summary = useMemo(() => ({
    total: promotions.length,
    active: promotions.filter((promo) => promo.isCurrentlyActive).length,
    gifts: promotions.filter((promo) => promo.type === "free-gift").length,
    used: promotions.reduce((sum, promo) => sum + Number(promo.usageCount || 0), 0),
  }), [promotions]);

  if (loading) return <div style={{ padding: 24 }}>Loading promotions...</div>;
  if (error) return <div style={{ padding: 24, color: "red" }}>{error}</div>;

  return (
    <div style={{ padding: 24, display: "grid", gap: 18 }}>
      <div>
        <h2 style={{ margin: 0 }}>Offers and Gifts Control</h2>
        <p style={{ color: "#64748b", marginTop: 6 }}>
          Track seller coupons, free gifts, first-order offers, and shipping campaigns from one place.
        </p>
      </div>

      <div style={summaryGrid}>
        {[
          ["Total Offers", summary.total],
          ["Active Now", summary.active],
          ["Free Gift Offers", summary.gifts],
          ["Total Uses", summary.used],
        ].map(([label, value]) => (
          <button
            key={label}
            type="button"
            style={summaryCard}
            onClick={() => {
              if (label === "Total Offers") setUrlState({ filter: "all" });
              if (label === "Active Now") setUrlState({ filter: "active" });
              if (label === "Free Gift Offers") setUrlState({ filter: "free-gift" });
            }}
          >
            <div style={muted}>{label}</div>
            <strong style={{ fontSize: 28 }}>{value}</strong>
            <div style={{ color: "#0284c7", fontSize: 12, marginTop: 6, fontWeight: 800 }}>Open list</div>
          </button>
        ))}
      </div>

      <section style={guideBox}>
        <strong>How offers work</strong>
        <p style={{ margin: "6px 0 0" }}>
          Sellers create offers from their dashboard. First-order, free-shipping, and free-gift offers apply automatically
          when cart rules match. Coupon offers apply when the customer enters the code at checkout. Super admin uses this
          page to audit active offers, usage, seller, audience, and expiry.
        </p>
      </section>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <input
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setUrlState({ q: event.target.value });
          }}
          placeholder="Search offer, coupon, seller..."
          style={{ ...input, flex: "1 1 260px" }}
        />
        <select value={filter} onChange={(event) => setUrlState({ filter: event.target.value })} style={input}>
          <option value="all">All offers</option>
          <option value="active">Active now</option>
          <option value="inactive">Inactive / expired</option>
          <option value="coupon">Coupons</option>
          <option value="first-order">First order</option>
          <option value="free-shipping">Free shipping</option>
          <option value="free-gift">Free gifts</option>
          <option value="retail">Retail audience</option>
          <option value="wholesale">Wholesale audience</option>
        </select>
        <select value={sort} onChange={(event) => setUrlState({ sort: event.target.value })} style={input}>
          <option value="newest">Newest first</option>
          <option value="usage-high">Most used</option>
          <option value="ending-soon">Ending soon</option>
          <option value="seller">Seller A-Z</option>
        </select>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={table}>
          <thead>
            <tr style={{ background: "#f8fafc", textAlign: "left" }}>
              <th style={th}>Offer</th>
              <th style={th}>Seller</th>
              <th style={th}>Rules</th>
              <th style={th}>Window</th>
              <th style={th}>Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((promo) => (
              <tr key={promo._id} style={{ borderBottom: "1px solid #e2e8f0" }}>
                <td style={td}>
                  <strong>{promo.title}</strong>
                  <div style={muted}>{promo.type}{promo.code ? ` • ${promo.code}` : ""}</div>
                  <div style={{ color: "#0f766e", fontWeight: 800, marginTop: 4 }}>{promo.label}</div>
                </td>
                <td style={td}>
                  <strong>{promo.seller?.storeName || promo.seller?.name || "Seller"}</strong>
                  <div style={muted}>{promo.seller?.email || "No email"}</div>
                </td>
                <td style={td}>
                  <div>Audience: {promo.audience || "retail"}</div>
                  <div>Min order: Rs.{Number(promo.minOrderValue || 0).toFixed(0)}</div>
                  <div>Used: {promo.usageCount || 0}</div>
                  {promo.freeGiftLabel ? <div>Gift: {promo.freeGiftLabel}</div> : null}
                </td>
                <td style={td}>
                  <div>{promo.startDate ? new Date(promo.startDate).toLocaleDateString() : "Starts now"}</div>
                  <div>{promo.endDate ? new Date(promo.endDate).toLocaleDateString() : "No end date"}</div>
                </td>
                <td style={td}>
                  <span style={{
                    ...pill,
                    background: promo.isCurrentlyActive ? "#dcfce7" : "#fee2e2",
                    color: promo.isCurrentlyActive ? "#166534" : "#991b1b",
                  }}>
                    {promo.isCurrentlyActive ? "Active" : "Not active"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filtered.length === 0 ? <p>No promotions found for this filter.</p> : null}
    </div>
  );
};

const summaryGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 12,
};
const summaryCard = {
  textAlign: "left",
  cursor: "pointer",
  background: "#fff",
  border: "1px solid #dbeafe",
  borderRadius: 18,
  padding: 16,
  boxShadow: "0 10px 30px rgba(15,23,42,0.06)",
};
const guideBox = {
  background: "#f0fdfa",
  border: "1px solid #99f6e4",
  borderRadius: 16,
  padding: 14,
  color: "#134e4a",
};
const table = {
  width: "100%",
  borderCollapse: "collapse",
  minWidth: 900,
  background: "#fff",
  borderRadius: 16,
  overflow: "hidden",
  boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
};
const th = { padding: "12px 14px" };
const td = { padding: "12px 14px", verticalAlign: "top" };
const muted = { color: "#64748b", fontSize: 13 };
const input = { padding: "10px 12px", border: "1px solid #cbd5e1", borderRadius: 12 };
const pill = { display: "inline-flex", borderRadius: 999, padding: "6px 10px", fontWeight: 800 };

export default AdminPromotions;
